import { App as CapacitorApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { type ReactNode, Suspense, useEffect } from "react";
import { Route, Switch, useHistory } from "react-router-dom";
import { LoadingScreen } from "@/components/ui/loading-screen";
import {
  closeBrowserForConnectReturn,
  handleDeepLinkOpen,
} from "@/lib/deep-link";
import { useDesktopSupportLink } from "@/lib/desktop";
import { useKeyboardInset } from "@/lib/hooks/use-keyboard-inset";
import { useRegistrationOutbox } from "@/lib/hooks/use-registration-outbox";
import { lazyChunk } from "@/lib/lazy-chunk";
import { usePushRegistration } from "@/lib/notifications/use-push-registration";
import { useAppSession } from "@/lib/offline-session";
import { routeTemplates } from "@/lib/routes";
import { ActiveCallPage } from "./active-call";
import { AuthGate } from "./auth-gate";
import { IncomingCallPage } from "./incoming-call";

const SignIn = lazyChunk(() => import("@/auth/sign-in"));
const OAuthConsent = lazyChunk(() => import("@/auth/oauth-consent"));

// Eager, unlike its neighbours: /signup is prerendered (ADR-0112), so the
// reader is already looking at this form when the bundle boots. Behind `lazy`
// its chunk is a second round trip that React covers with a full-bleed
// spinner — and it is not in the shell's modulepreloads, so the served page
// would go form → spinner → form on exactly the slow connections prerendering
// is for. The component is ~4kB against a ~1.3MB entry; the round trip costs
// far more than the bytes. A prerendered route must not be lazily imported.
import SignUp from "@/auth/sign-up";

const ForgotPassword = lazyChunk(() => import("@/auth/forgot-password"));
const ResetPassword = lazyChunk(() => import("@/auth/reset-password"));
const VerifyEmail = lazyChunk(() => import("@/auth/verify-email"));

const DefaultAppRedirect = lazyChunk(
  () => import("@/pages/routes/default-app-redirect"),
  "DefaultAppRedirect"
);
const AppShell = lazyChunk(() => import("@/pages/app/app-shell"), "AppShell");

const LinkRedirect = lazyChunk(
  () => import("@/pages/routes/link-redirect"),
  "LinkRedirect"
);

const MrzScannerPage = lazyChunk(() => import("@/shell/mrz-scanner"));

const TaskLiveView = lazyChunk(
  () => import("@/components/tasks/task-live-view"),
  "TaskLiveView"
);

function AuthFallback() {
  return <LoadingScreen />;
}

/**
 * The shape most routes below take: behind `AuthGate`, with a Suspense
 * fallback for the lazy page. Written as a `Route` wrapper rather than an
 * inner one so the deliberate exceptions read as exceptions: they are the
 * routes that do *not* say `GatedRoute`. `/oauth/consent` skips the gate;
 * the two call screens skip Suspense because they are eager imports. Each
 * says why at its own route. The channel connect pages are not here at all:
 * they boot under `ConnectApp`, which never mounts this shell.
 *
 * `Switch` picks its child by reading `path` / `exact` off the element's
 * props, so those have to stay named exactly that here; it then clones the
 * winner with a `computedMatch` this wrapper drops, and the inner `Route`
 * simply matches the same path again.
 */
function GatedRoute({
  children,
  exact,
  path,
}: {
  children: ReactNode;
  exact?: boolean;
  path: string;
}) {
  return (
    <Route exact={exact} path={path}>
      <AuthGate>
        <Suspense fallback={<AuthFallback />}>{children}</Suspense>
      </AuthGate>
    </Route>
  );
}

export function App() {
  const history = useHistory();

  // Native push registration + tap deep-linking.
  usePushRegistration();

  // Mirror the strip the keyboard covers into `--keyboard-height` so
  // bottom-docked composers can ride above the keyboard instead of behind it.
  useKeyboardInset();

  // Send ID captures an older bundle queued when their registration POST
  // didn't land.
  useRegistrationOutbox();

  useDesktopSupportLink();

  // The App Group's queued taps and auth context belong to one account, so
  // they are pushed and drained as the signed-in one, as soon as it is known
  // and again on every foreground. The push goes first: it is what claims taps
  // an older build queued in this session.
  const userId = useAppSession().data?.user.id ?? null;
  useEffect(() => {
    if (!(Capacitor.isNativePlatform() && userId)) {
      return;
    }
    const onActive = async () => {
      const { flushPendingLiveActivityActions, pushLiveActivityAuthContext } =
        await import("@/lib/tasks/task-activity");
      await pushLiveActivityAuthContext(userId);
      await flushPendingLiveActivityActions(userId);
    };
    void onActive();
    const resume = CapacitorApp.addListener(
      "appStateChange",
      ({ isActive }) => {
        if (isActive) {
          void onActive();
        }
      }
    );
    return () => {
      void resume.then((handle) => handle.remove());
    };
  }, [userId]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    const handles: Array<{ remove: () => void }> = [];

    void (async () => {
      const [{ applyLiveActivityDeepLink }, { setupLiveActivityPushTokens }] =
        await Promise.all([
          import("@/lib/tasks/task-activity"),
          import("@/lib/tasks/live-activity-mirror"),
        ]);

      // Register each activity's APNs push token with the server so it can push
      // content-state updates to a locked card. Awaited (not fire-and-
      // forget) so the listener is attached before a relaunch can re-emit a
      // cached token; idempotent across mounts.
      await setupLiveActivityPushTokens();

      handles.push(
        await CapacitorApp.addListener("appUrlOpen", ({ url }) => {
          // Allowlist + entity-gate every inbound `vidimo://` link; unknown,
          // malformed, or unsolicited URLs are ignored. Dispatch logic
          // lives in `handleDeepLinkOpen` (unit-tested); this just wires it to
          // the task-action write and in-app navigation.
          handleDeepLinkOpen(url, {
            applyTaskAction: applyLiveActivityDeepLink,
            closeBrowser: () =>
              closeBrowserForConnectReturn(Capacitor.getPlatform(), () =>
                Browser.close()
              ),
            navigate: (path) => history.push(path),
          });
        })
      );
    })();

    return () => {
      for (const h of handles) {
        h.remove();
      }
    };
  }, [history]);

  return (
    <Switch>
      {/* Public auth routes */}
      <GatedRoute path={routeTemplates.login}>
        <SignIn />
      </GatedRoute>
      {/* OAuth consent for the MCP connector flow. Deliberately outside
          AuthGate: the page handles its own signed-out state, and the gate's
          public/private redirects would strip the consent query. */}
      <Route path="/oauth/consent">
        <Suspense fallback={<AuthFallback />}>
          <OAuthConsent />
        </Suspense>
      </Route>
      <GatedRoute path={routeTemplates.signup}>
        <SignUp />
      </GatedRoute>
      <GatedRoute path={routeTemplates.forgotPassword}>
        <ForgotPassword />
      </GatedRoute>
      <GatedRoute path={routeTemplates.resetPassword}>
        <ResetPassword />
      </GatedRoute>
      <GatedRoute path={routeTemplates.verifyEmail}>
        <VerifyEmail />
      </GatedRoute>

      <GatedRoute exact path="/mrz-scan">
        <div className="flex h-dvh min-h-0 flex-col overflow-hidden bg-background">
          <MrzScannerPage />
        </div>
      </GatedRoute>

      {/* Incoming-call screen — uses `useVoice()`, which only has a provider
          inside AuthenticatedShell (mounted by main.tsx after sign-in).
          AuthGate prevents the unauthenticated tree from rendering this and
          crashing with "useVoice must be used within VoiceProvider". */}
      <Route exact path={routeTemplates.app.incomingCall}>
        <AuthGate>
          <IncomingCallPage />
        </AuthGate>
      </Route>

      {/* Active-call screen (maximised view of the bottom overlay) — same
          provider gating as /incoming-call. */}
      <Route exact path={routeTemplates.app.activeCall}>
        <AuthGate>
          <ActiveCallPage />
        </AuthGate>
      </Route>

      {/* Full-screen task execution view — sits outside the tab shell
          (no bottom nav), like the scanner. Prep review + Start lives here. */}
      <GatedRoute exact path={routeTemplates.app.taskLive}>
        <TaskLiveView />
      </GatedRoute>

      {/* Property-agnostic docs deep link (`/link/:target`) — resolves the
          reader's current property and redirects into the real settings
          screen. Private: a signed-out reader is bounced to /login with the
          destination preserved as `?next=` (AuthGate), so they land here again
          after signing in. */}
      <GatedRoute exact path="/link/:target">
        <LinkRedirect />
      </GatedRoute>

      {/* Root redirect */}
      <GatedRoute exact path="/">
        <DefaultAppRedirect />
      </GatedRoute>

      {/* All in-app tabs share a single shell. Data filtering is per-view
          (each list/feed surface has its own Filter drawer); routes are flat
          (no :orgSlug/:propertySlug). */}
      <GatedRoute path="/">
        <AppShell />
      </GatedRoute>
    </Switch>
  );
}
